import type { AppSettingsObject, FullstoryApi, FullstoryProperties } from '~/types/global';

type FullstoryCall =
  | { method: 'trackEvent'; options: { name: string; properties: FullstoryProperties } }
  | { method: 'setProperties'; options: { type: 'page'; properties: FullstoryProperties } };

type QueuedFullstoryCall = FullstoryCall & { path: string };

const FULLSTORY_QUEUE_LIMIT = 20;
const FULLSTORY_RETRY_INTERVAL_MS = 500;
const FULLSTORY_RETRY_ATTEMPTS = 20;

// GTM loads Fullstory only after analytics consent, so calls made before that (e.g. from mount effects on a
// hard landing) wait here. The check restarts on every consent change, so a late banner accept still flushes them.
// Never stub window.FS instead: the snippet bails out when the namespace already exists.
let fullstoryQueue: QueuedFullstoryCall[] = [];
let fullstoryRetryTimer: ReturnType<typeof setInterval> | undefined;
let isConsentListenerAdded = false;

const getFullstory = (): FullstoryApi | undefined => {
  const globals = window as unknown as Record<string, unknown> & { _fs_namespace?: unknown };
  const namespace = typeof globals._fs_namespace === 'string' ? globals._fs_namespace : 'FS';
  const fullstory = globals[namespace];

  return typeof fullstory === 'function' ? (fullstory as FullstoryApi) : undefined;
};

const sendToFullstory = (fullstory: FullstoryApi, call: FullstoryCall) => {
  if (call.method === 'trackEvent') {
    fullstory('trackEvent', call.options);
  } else {
    fullstory('setProperties', call.options);
  }
};

// Fullstory attributes a call to the page open when it arrives, so calls queued on another page are dropped
const flushFullstoryQueue = (fullstory: FullstoryApi) => {
  const currentPath = window.location.pathname;
  const queuedCalls = fullstoryQueue;

  fullstoryQueue = [];
  queuedCalls
    .filter(({ path }) => path === currentPath)
    .forEach(call => sendToFullstory(fullstory, call));
};

const stopFullstoryRetry = () => {
  clearInterval(fullstoryRetryTimer);
  fullstoryRetryTimer = undefined;
};

const startFullstoryRetry = () => {
  if (fullstoryRetryTimer) {
    return;
  }

  let attempts = 0;

  fullstoryRetryTimer = setInterval(() => {
    attempts += 1;

    const fullstory = getFullstory();

    if (fullstory) {
      stopFullstoryRetry();
      flushFullstoryQueue(fullstory);
    } else if (attempts >= FULLSTORY_RETRY_ATTEMPTS) {
      stopFullstoryRetry();
    }
  }, FULLSTORY_RETRY_INTERVAL_MS);
};

const listenForConsentChange = () => {
  if (isConsentListenerAdded) {
    return;
  }

  isConsentListenerAdded = true;
  window.addEventListener('OneTrustGroupsUpdated', () => {
    if (fullstoryQueue.length > 0) {
      startFullstoryRetry();
    }
  });
};

const callFullstory = (call: FullstoryCall) => {
  if (typeof window === 'undefined') {
    return;
  }

  const fullstory = getFullstory();

  if (fullstory) {
    stopFullstoryRetry();
    flushFullstoryQueue(fullstory);
    sendToFullstory(fullstory, call);

    return;
  }

  fullstoryQueue = [...fullstoryQueue, { ...call, path: window.location.pathname }].slice(
    -FULLSTORY_QUEUE_LIMIT
  );
  listenForConsentChange();
  startFullstoryRetry();
};

export const trackFullstoryEvent = (name: string, properties: FullstoryProperties = {}): void => {
  callFullstory({ method: 'trackEvent', options: { name, properties } });
};

export const setFullstoryPageProperties = (properties: FullstoryProperties): void => {
  callFullstory({ method: 'setProperties', options: { type: 'page', properties } });
};

export const pushToAppSettings = (appSettings: AppSettingsObject) => {
  window.AppSettings = window.AppSettings ?? {};
  window.AppSettings = { ...window.AppSettings, ...appSettings };
};
